import React, { useCallback, useState } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	Stack,
	Tooltip,
	Typography
} from '@mui/material';
import { alpha, styled, keyframes } from '@mui/material/styles';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import TvRoundedIcon from '@mui/icons-material/TvRounded';
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import LanguageSwitcher from '../../common/LanguageSwitcher/LanguageSwitcher';
import queueTicketService from '../../services/queueTicketService';
import { useWebSocket } from '../../common/hooks/useWebSocket';

const QUEUE_TOPIC = '/topic/queue-events';

function TicketSuccess() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const [params] = useSearchParams();

	const initialTicketNumber =
		location?.state?.ticketNumber || params.get('ticket') || '—';

	const [ticketNumber, setTicketNumber] = useState(initialTicketNumber);
	const [trackingToken, setTrackingToken] = useState(
		location?.state?.trackingToken ||
			(initialTicketNumber !== '—'
				? sessionStorage.getItem(`ticket_token_${initialTicketNumber}`)
				: null) ||
			null
	);
	const [fullName, setFullName] = useState(location?.state?.fullName || '');
	const [program, setProgram] = useState(location?.state?.program || '');

	const [copied, setCopied] = useState(false);
	const [cancelOpen, setCancelOpen] = useState(false);
	const [cancelling, setCancelling] = useState(false);
	const [cancelled, setCancelled] = useState(false);
	const [cancelError, setCancelError] = useState('');

	// Менеджер может перенаправить талон на другой вид услуг — тогда создаётся
	// новый WAITING-талон. Слушаем WS и подменяем отображаемый талон на новый.
	const handleWsEvent = useCallback(
		(event) => {
			if (event?.eventType !== 'TICKET_REDIRECTED') return;
			if (event?.previousTicketNumber !== ticketNumber) return;

			const next = event.ticket;
			if (!next?.ticketNumber) return;

			sessionStorage.removeItem(`ticket_token_${ticketNumber}`);
			setTicketNumber(next.ticketNumber);
			if (next.trackingToken) {
				setTrackingToken(next.trackingToken);
				sessionStorage.setItem(`ticket_token_${next.ticketNumber}`, next.trackingToken);
			}
			if (next.fullName) setFullName(next.fullName);
			if (next.program) setProgram(next.program);
			setCancelled(false);
			toast.info(t('ticketSuccess.redirectedNotice', { number: next.ticketNumber }));
		},
		[ticketNumber, t]
	);

	useWebSocket(QUEUE_TOPIC, handleWsEvent);

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(ticketNumber);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch {
			// no-op
		}
	};

	const handleCancelConfirm = async () => {
		setCancelling(true);
		setCancelError('');
		try {
			await queueTicketService.cancelTicket(ticketNumber, trackingToken);
			sessionStorage.removeItem(`ticket_token_${ticketNumber}`);
			setCancelled(true);
			setCancelOpen(false);
		} catch (err) {
			const msg = err?.response?.data?.message || t('ticketSuccess.cancelError');
			setCancelError(msg);
		} finally {
			setCancelling(false);
		}
	};

	return (
		<PageRoot>
			<BgCircle sx={{ width: 400, height: 400, top: -120, right: -120, opacity: 0.1 }} />
			<BgCircle sx={{ width: 250, height: 250, bottom: -60, left: -80, opacity: 0.07 }} />

			<Content>
				{/* Логотип + переключатель языка */}
				<Stack alignItems='center' spacing={1} sx={{ mb: 3 }}>
					<Logo src='/logo-white.png' alt='Dulaty University' />
					<Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>
						{t('ticketSuccess.university')}
					</Typography>
					<LanguageSwitcher variant='dark' />
				</Stack>

				{/* Иконка успеха */}
				<Stack alignItems='center' sx={{ mb: 2 }}>
					<SuccessIcon>
						<CheckCircleRoundedIcon sx={{ fontSize: 52, color: '#fff' }} />
					</SuccessIcon>
					<Typography sx={{ fontWeight: 800, fontSize: 20, color: '#fff', mt: 2, textAlign: 'center', lineHeight: 1.3 }}>
						{t('ticketSuccess.successTitle')}
					</Typography>
					<Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', mt: 0.75, textAlign: 'center' }}>
						{t('ticketSuccess.successSubtitle')}
					</Typography>
				</Stack>

				{/* Карточка с номером талона */}
				<TicketCard>
					<Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 2.5, color: 'text.secondary', mb: 1 }}>
						{t('ticketSuccess.ticketLabel')}
					</Typography>

					<Stack direction='row' alignItems='center' justifyContent='center' spacing={1}>
						<TicketNumber>{ticketNumber}</TicketNumber>
						{ticketNumber !== '—' && (
							<Tooltip title={copied ? t('ticketSuccess.copiedTooltip') : t('ticketSuccess.copyTooltip')} placement='top'>
								<IconButton
									onClick={handleCopy}
									size='small'
									sx={{
										color: copied ? 'success.main' : 'primary.main',
										bgcolor: copied ? alpha('#4caf50', 0.1) : alpha('#1976d2', 0.08),
										transition: 'all .2s',
										'&:hover': { bgcolor: copied ? alpha('#4caf50', 0.18) : alpha('#1976d2', 0.15) }
									}}
								>
									{copied ? <CheckRoundedIcon fontSize='small' /> : <ContentCopyIcon fontSize='small' />}
								</IconButton>
							</Tooltip>
						)}
					</Stack>

					{(fullName || program) && (
						<Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
							{fullName && (
								<Typography variant='body2' color='text.secondary' sx={{ fontSize: 13, fontWeight: 600 }}>
									{fullName}
								</Typography>
							)}
							{program && (
								<Typography variant='body2' color='text.secondary' sx={{ fontSize: 12, mt: 0.5 }}>
									{program}
								</Typography>
							)}
						</Box>
					)}

					<Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
						<ConfirmationNumberRoundedIcon sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0, mt: 0.2 }} />
						<Typography variant='body2' color='text.secondary' sx={{ fontSize: 13, lineHeight: 1.5 }}>
							{t('ticketSuccess.ticketHint')}
						</Typography>
					</Box>
				</TicketCard>

				{/* Кнопки действий */}
				<Stack spacing={1.5} sx={{ mt: 2 }}>
					<ActionButton
						variant='contained'
						fullWidth
						startIcon={<TvRoundedIcon />}
						onClick={() => navigate('/')}
						sx={{ background: 'rgba(255,255,255,0.18)', color: '#fff', '&:hover': { background: 'rgba(255,255,255,0.26)' } }}
					>
						{t('ticketSuccess.watchQueue')}
					</ActionButton>

					<ActionButton
						variant='outlined'
						fullWidth
						startIcon={<HomeRoundedIcon />}
						onClick={() => navigate('/new-token')}
						sx={{
							borderColor: 'rgba(255,255,255,0.35)',
							color: 'rgba(255,255,255,0.75)',
							'&:hover': { borderColor: 'rgba(255,255,255,0.6)', color: '#fff', background: 'rgba(255,255,255,0.06)' }
						}}
					>
						{t('ticketSuccess.getAnother')}
					</ActionButton>

					{trackingToken && !cancelled && ticketNumber !== '—' && (
						<ActionButton
							variant='outlined'
							fullWidth
							startIcon={<CancelRoundedIcon />}
							onClick={() => setCancelOpen(true)}
							sx={{
								borderColor: 'rgba(255,255,255,0.25)',
								color: 'rgba(255,255,255,0.5)',
								'&:hover': { borderColor: 'rgba(255,255,255,0.45)', color: 'rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.05)' }
							}}
						>
							{t('ticketSuccess.cancelTicket')}
						</ActionButton>
					)}

					{cancelled && (
						<Typography sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.75)', fontSize: 14, fontWeight: 600 }}>
							{t('ticketSuccess.cancelledMessage')}
						</Typography>
					)}
				</Stack>
			</Content>

			{/* Диалог подтверждения отмены */}
			<Dialog open={cancelOpen} onClose={() => !cancelling && setCancelOpen(false)} PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
				<DialogTitle sx={{ fontWeight: 800 }}>{t('ticketSuccess.cancelDialogTitle')}</DialogTitle>
				<DialogContent>
					<DialogContentText>{t('ticketSuccess.cancelDialogText', { number: ticketNumber })}</DialogContentText>
					{cancelError && (
						<Typography color='error' sx={{ mt: 1, fontSize: 13 }}>{cancelError}</Typography>
					)}
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
					<Button onClick={() => setCancelOpen(false)} disabled={cancelling} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
						{t('common.cancel')}
					</Button>
					<Button
						onClick={handleCancelConfirm}
						disabled={cancelling}
						variant='contained'
						color='error'
						sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, minWidth: 100 }}
					>
						{cancelling ? <CircularProgress size={18} color='inherit' /> : t('ticketSuccess.cancelConfirm')}
					</Button>
				</DialogActions>
			</Dialog>
		</PageRoot>
	);
}

/* ── Стили ──────────────────────────────────────────────────── */

const popIn = keyframes`
	0%   { transform: scale(0.5); opacity: 0; }
	70%  { transform: scale(1.12); }
	100% { transform: scale(1);   opacity: 1; }
`;

const slideUp = keyframes`
	from { transform: translateY(32px); opacity: 0; }
	to   { transform: translateY(0);    opacity: 1; }
`;

const PageRoot = styled(Box)(({ theme }) => ({
	minHeight: '100vh',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: theme.spacing(2),
	background: `linear-gradient(150deg,
		${alpha(theme.palette.primary.dark, 0.97)} 0%,
		${alpha(theme.palette.primary.main, 0.88)} 55%,
		${alpha(theme.palette.primary.light, 0.75)} 100%)`,
	position: 'relative',
	overflow: 'hidden'
}));

const Content = styled(Box)(() => ({
	width: '100%',
	maxWidth: 400,
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'stretch',
	position: 'relative',
	zIndex: 1,
	animation: `${slideUp} 0.5s cubic-bezier(0.4, 0, 0.2, 1) both`
}));

const SuccessIcon = styled(Box)(({ theme }) => ({
	width: 88,
	height: 88,
	borderRadius: '50%',
	backgroundColor: alpha(theme.palette.success.main, 0.9),
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	boxShadow: `0 0 0 10px ${alpha(theme.palette.success.main, 0.2)},
	            0 0 0 20px ${alpha(theme.palette.success.main, 0.08)}`,
	animation: `${popIn} 0.55s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both`
}));

const TicketCard = styled(Box)(({ theme }) => ({
	backgroundColor: theme.palette.background.paper,
	borderRadius: 24,
	padding: theme.spacing(3),
	textAlign: 'center',
	boxShadow: '0 20px 60px rgba(0,0,0,0.25)'
}));

const TicketNumber = styled(Typography)(({ theme }) => ({
	fontWeight: 900,
	fontSize: 'clamp(56px, 18vw, 80px)',
	lineHeight: 1,
	letterSpacing: '0.04em',
	fontVariantNumeric: 'tabular-nums',
	color: theme.palette.primary.main,
	animation: `${popIn} 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.3s both`
}));

const ActionButton = styled(Button)(() => ({
	height: 52,
	borderRadius: 14,
	fontSize: 15,
	fontWeight: 700,
	textTransform: 'none',
	boxShadow: 'none',
	'&:hover': { boxShadow: 'none' }
}));

const Logo = styled('img')(() => ({
	width: 60,
	height: 60,
	objectFit: 'contain'
}));

const BgCircle = styled(Box)(() => ({
	position: 'absolute',
	borderRadius: '50%',
	backgroundColor: '#fff',
	pointerEvents: 'none'
}));

export default TicketSuccess;
